import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

import type { TooltipContentProps } from "recharts";

export interface TrendPoint {
    /** ISO date `YYYY-MM-DD`. */
    date: string;
    sessions: number;
    minutes: number;
}

interface TrendBarChartProps {
    data: TrendPoint[];
    /** Label sumbu X (nama hari pendek dsb) — diteruskan dari pemanggil. */
    formatXLabel: (date: string) => string;
    /** Format menit jadi teks durasi ("2h 30m"). */
    formatDuration: (minutes: number) => string;
    sessionsLabel: string;
    durationLabel: string;
    height?: number;
}

/**
 * Grafik batang tren harian (jumlah sesi per hari).
 *
 * Reusable untuk menu lain: cukup kirim `TrendPoint[]` + formatter. Warna
 * mengikuti CSS variable tema supaya otomatis benar di mode gelap.
 */
export function TrendBarChart({
    data,
    formatXLabel,
    formatDuration,
    sessionsLabel,
    durationLabel,
    height = 260,
}: TrendBarChartProps) {
    return (
        <div style={{ height }} className="w-full">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
                    <defs>
                        <linearGradient id="trendBarGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop
                                offset="0%"
                                stopColor="var(--color-primary)"
                                stopOpacity={0.95}
                            />
                            <stop
                                offset="100%"
                                stopColor="var(--color-primary-light)"
                                stopOpacity={0.55}
                            />
                        </linearGradient>
                    </defs>

                    <CartesianGrid
                        strokeDasharray="4 4"
                        stroke="var(--app-shell-border)"
                        vertical={false}
                    />
                    <XAxis
                        dataKey="date"
                        tickFormatter={formatXLabel}
                        tickLine={false}
                        axisLine={false}
                        tick={{ fill: "var(--app-text-soft)", fontSize: 12, fontWeight: 500 }}
                    />
                    <YAxis
                        allowDecimals={false}
                        tickLine={false}
                        axisLine={false}
                        tick={{ fill: "var(--app-text-soft)", fontSize: 12 }}
                    />
                    <Tooltip
                        cursor={{ fill: "var(--app-content-subtle-bg)" }}
                        content={(props: TooltipContentProps) => (
                            <TrendTooltip
                                props={props}
                                formatXLabel={formatXLabel}
                                formatDuration={formatDuration}
                                sessionsLabel={sessionsLabel}
                                durationLabel={durationLabel}
                            />
                        )}
                    />
                    <Bar
                        dataKey="sessions"
                        fill="url(#trendBarGradient)"
                        radius={[8, 8, 0, 0]}
                        maxBarSize={46}
                        animationDuration={700}
                    >
                        {data.map((point) => (
                            <Cell
                                key={point.date}
                                fill={
                                    point.sessions > 0
                                        ? "url(#trendBarGradient)"
                                        : "var(--app-content-subtle-bg)"
                                }
                            />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

function TrendTooltip({
    props,
    formatXLabel,
    formatDuration,
    sessionsLabel,
    durationLabel,
}: {
    props: TooltipContentProps;
    formatXLabel: (date: string) => string;
    formatDuration: (minutes: number) => string;
    sessionsLabel: string;
    durationLabel: string;
}) {
    const point = props.payload?.[0]?.payload as TrendPoint | undefined;
    if (!point) return null;

    return (
        <div className="app-content-card rounded-xl border px-3 py-2 shadow-lg">
            <p className="app-text-primary text-xs font-semibold">
                {formatXLabel(point.date)}
            </p>
            <p className="app-shell-text-muted mt-1 text-xs">
                {sessionsLabel}: <span className="app-text-primary font-semibold">{point.sessions}</span>
            </p>
            <p className="app-shell-text-muted text-xs">
                {durationLabel}:{" "}
                <span className="app-text-primary font-semibold">
                    {formatDuration(point.minutes)}
                </span>
            </p>
        </div>
    );
}
